import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, Plus, Search } from 'lucide-react';
import { Button } from '@/components/Button';
import { Card, CardBody } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { Skeleton, SkeletonRegion } from '@/components/Skeleton';
import { cn } from '@/lib/cn';
import { formatarDistancia } from '@/lib/format';
import { AvisoDeFalha } from '@/features/workflow/componentes';
import { classificarFalha } from '@/features/workflow/recusa';
import {
  chavesDeConhecimento,
  listarArtigos,
  type FiltroDeArtigos,
} from '@/features/knowledge/conhecimento.api';
import {
  EtiquetaDeRevisao,
  EtiquetaDeSituacao,
  EtiquetaDeVisibilidade,
  Utilidade,
} from '@/features/knowledge/componentes';
import {
  CAMINHO_ARTIGO_NOVO,
  LinkDoArtigo,
  useBuscaDeArtigos,
  useIrPara,
} from '@/features/knowledge/navegacao';
import {
  ROTULO_DE_SITUACAO,
  SITUACOES_DE_ARTIGO,
  type SituacaoDeArtigo,
} from '@/features/knowledge/tipos';

const ESPERA_DA_BUSCA = 300;

/**
 * A base de conhecimento — lista e busca.
 *
 * ## Uma nota honesta sobre esta busca
 *
 * Diferente da lista da KEDB, o campo aqui **não** é `ILIKE`. Ele vai ao índice
 * invertido: radicaliza o que foi digitado pelo mesmo dicionário português que
 * indexou os artigos, trata a última palavra como prefixo enquanto se digita, e
 * resolve tudo dentro do índice mesmo com a RLS ligada.
 *
 * Isso importa para quem usa: a busca acha "impressora" digitando "impres", e
 * acha o artigo cujo título diz "impressoras" no plural. Uma busca literal não
 * faria nem uma coisa nem outra.
 */
export function ConhecimentoPage() {
  const { termo, definir } = useBuscaDeArtigos();
  const [texto, setTexto] = useState(termo);
  const [status, setStatus] = useState<SituacaoDeArtigo | 'TODOS'>('TODOS');
  const [somenteVencidos, setSomenteVencidos] = useState(false);
  const navegar = useIrPara();

  /**
   * Digitar espera antes de navegar. `definir` fica numa referência e fora das
   * dependências: o hook devolve uma função nova a cada renderização, e
   * listá-la reiniciaria o relógio em toda renderização — uma espera que
   * reinicia sozinha é uma espera que pode nunca vencer.
   */
  const definirAgora = useRef(definir);
  definirAgora.current = definir;
  useEffect(() => {
    const relogio = setTimeout(() => {
      if (texto.trim() !== termo) definirAgora.current(texto.trim());
    }, ESPERA_DA_BUSCA);
    return () => clearTimeout(relogio);
  }, [texto, termo]);

  const filtro: FiltroDeArtigos = {
    ...(termo ? { q: termo } : {}),
    ...(status !== 'TODOS' ? { status } : {}),
    ...(somenteVencidos ? { reviewDue: true } : {}),
  };

  const consulta = useQuery({
    queryKey: chavesDeConhecimento.lista(filtro),
    queryFn: () => listarArtigos(filtro),
  });

  const artigos = consulta.data ?? [];

  return (
    <div className="mx-auto max-w-[1400px] space-y-3">
      <header className="flex items-center justify-between gap-4 border-b border-border pb-3">
        <h1 className="text-base font-semibold">Base de conhecimento</h1>
        <Button onClick={() => navegar({ to: CAMINHO_ARTIGO_NOVO })}>
          <Plus className="mr-1.5 h-4 w-4" aria-hidden />
          Novo artigo
        </Button>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <label className="relative min-w-[16rem] flex-1">
          <span className="sr-only">Buscar na base de conhecimento</span>
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <input
            type="search"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="Buscar por título, resumo, corpo ou palavra-chave…"
            className={cn(
              'h-9 w-full rounded-lg border border-input/80 bg-muted/40 pl-9 pr-3 text-sm',
              'transition-colors placeholder:text-muted-foreground hover:bg-muted',
              'focus-visible:border-ring/40 focus-visible:bg-background focus-visible:outline-none focus-visible:shadow-glow',
            )}
          />
        </label>

        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as SituacaoDeArtigo | 'TODOS')}
          className="h-9 rounded-lg border border-input/80 bg-muted/40 px-2 text-sm transition-colors hover:bg-muted focus-visible:border-ring/40 focus-visible:bg-background focus-visible:outline-none focus-visible:shadow-glow"
          aria-label="Filtrar por situação"
        >
          <option value="TODOS">Todas as situações</option>
          {SITUACOES_DE_ARTIGO.map((s) => (
            <option key={s} value={s}>
              {ROTULO_DE_SITUACAO[s]}
            </option>
          ))}
        </select>

        <label className="inline-flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={somenteVencidos}
            onChange={(e) => setSomenteVencidos(e.target.checked)}
            className="h-4 w-4 rounded border-border"
          />
          Só revisão vencida
        </label>
      </div>

      {consulta.isError && <AvisoDeFalha falha={classificarFalha(consulta.error)} />}

      {consulta.isPending ? (
        <SkeletonRegion label="Carregando artigos">
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-24 w-full rounded-lg" />
            ))}
          </div>
        </SkeletonRegion>
      ) : artigos.length === 0 ? (
        <EmptyState
          icon={<BookOpen className="h-7 w-7" aria-hidden />}
          title={termo ? 'Nada encontrado' : 'A base ainda está vazia'}
          description={
            termo
              ? 'Nenhum artigo casou com esse texto. Tente menos palavras — a busca já radicaliza plural e conjugação.'
              : 'O melhor momento para escrever um artigo é logo depois de resolver um chamado difícil: a solução está fresca e escrevê-la custa menos.'
          }
          action={
            <Button onClick={() => navegar({ to: CAMINHO_ARTIGO_NOVO })}>Escrever o primeiro</Button>
          }
        />
      ) : (
        <ul className="space-y-3">
          {artigos.map((artigo) => (
            <li key={artigo.id}>
              <LinkDoArtigo artigoId={artigo.id} className="block">
                <Card className="transition-shadow hover:shadow-card-hover">
                  <CardBody className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-xs text-muted-foreground">{artigo.code}</span>
                      <EtiquetaDeSituacao situacao={artigo.status} />
                      <EtiquetaDeVisibilidade visibilidade={artigo.visibility} />
                      <EtiquetaDeRevisao nextReviewAt={artigo.nextReviewAt} />
                      {artigo.category && (
                        <span className="text-xs text-muted-foreground">
                          {artigo.category.name}
                        </span>
                      )}
                    </div>
                    <h2 className="text-sm font-semibold text-foreground">{artigo.title}</h2>
                    <p className="line-clamp-2 text-sm text-muted-foreground">{artigo.summary}</p>
                    <div className="flex flex-wrap items-center gap-3 pt-1">
                      <Utilidade
                        resolvidos={artigo.resolvedCount}
                        usos={artigo.usageCount}
                        uteis={artigo.helpfulCount}
                        inuteis={artigo.notHelpfulCount}
                      />
                      {artigo.updatedAt && (
                        <span className="text-xs text-muted-foreground">
                          Alterado {formatarDistancia(artigo.updatedAt)}
                        </span>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </LinkDoArtigo>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
